<template>
  <b-modal
    v-if="timeLog"
    ref="timeLogModal"
    size="lg"
    ok-title="Submit"
    cancel-title="Cancel"
    cancel-variant="danger"
    @ok.prevent="handleModal"
    @cancel="clearTimeLogForm"
    @show="editOrCreate"
  >
    <template v-slot:modal-title>
      <b-row>
        <b-col>
          <strong class="modal-header-custom">Log Time</strong>
        </b-col>
      </b-row>
      <b-row class="mt-12">
        <b-col v-if="user">
          <em>Logged today:</em> {{ getLogsForToday(user) }}
        </b-col>
      </b-row>
    </template>
    <div>
      <b-row>
        <b-col>
          <b-form-group label="Client">
            <b-form-select v-model="timeLog.client" :options="clientOptions">
              <template v-slot:first>
                <option :value="null" disabled
                  >-- Please select a Client --</option
                >
              </template>
            </b-form-select>
          </b-form-group>
        </b-col>
        <b-col>
          <b-form-group label="Project">
            <b-form-select v-model="timeLog.project" :options="projectOptions">
              <template v-slot:first>
                <option :value="-1">Support</option>
              </template>
            </b-form-select>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <b-form-group label="Task">
            <b-form-input
              v-model="timeLog.task"
              class="timeLog-input"
              placeholder="Input task here"
            ></b-form-input>
          </b-form-group>
        </b-col>
        <b-col>
          <b-form-group label="Ticket">
            <b-form-select v-model="timeLog.ticket" :options="ticketOptions">
              <template v-slot:first>
                <option :value="null">-- Optional Ticket --</option>
              </template>
            </b-form-select>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <b-form-group label="Description">
            <b-form-textarea
              v-model="timeLog.description"
              placeholder="Please enter description"
            ></b-form-textarea>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <b-form-group label="Date">
            <v-date-picker
              v-model="timeLog.date"
              :first-day-of-week="1"
              :popover="{ placement: 'top', visibility: 'click' }"
              :masks="{ input: 'YYYY/MM/DD' }"
            />
          </b-form-group>
        </b-col>
        <b-col>
          <b-form-group label="Time">
            <VueTimePicker
              v-model="timeLog.time"
              hide-clear-button
              :minute-interval="15"
              manual-input
            ></VueTimePicker>
          </b-form-group>
        </b-col>
      </b-row>
      <b-form-group label="Billing">
        <b-form-select v-model="timeLog.billable" :options="billingOptions">
          <template v-slot:first>
            <option :value="null" disabled>-- Please select --</option>
          </template>
        </b-form-select>
      </b-form-group>
    </div>
  </b-modal>
</template>

<script>
import { mapGetters } from "vuex"
import VueTimePicker from "vue2-timepicker/src/vue-timepicker.vue"
import gql from "graphql-tag"
import lodash from "lodash"

export default {
  name: "TimeLogModal",
  components: {
    VueTimePicker
  },
  props: {
    task: {
      type: Object,
      default: null
    },
    parentClientOptions: {
      required: false,
      default: null,
      type: Array
    },
    editTimeLog: {
      required: false,
      default: null,
      type: Object
    },
    isTicket: {
      type: Boolean,
      default: false
    },
    clientId: {
      type: Number,
      default: null
    },
    quoteId: {
      type: Number,
      default: null
    }
  },
  apollo: {
    clients: {
      query: gql`
        query {
          clients {
            id
            name
            projects {
              project
              status
              id
            }
            tickets {
              id
              name
              tasks {
                id
                description
              }
            }
            sections {
              id
              name
            }
          }
        }
      `,
      update(data) {
        return data?.clients.sortBy(x => x.name)
      },
      skip() {
        //Only load query once modal has been opened
        return !this.loadClients || this.parentClientOptions
      }
    },
    user: {
      query: require("@/graphql/Account/GetUserWithTimeLogs.gql"),
      variables() {
        return {
          userId: this.getCurrentUser.id
        }
      },
      skip() {
        return !this.getCurrentUser?.id || !this.loadClients
      }
    }
  },
  data() {
    return {
      loadClients: false,
      timeLog: {
        client: null,
        project: -1, // Default to Support
        task: null,
        description: null,
        date: this.$moment()._d,
        time: { HH: "00", mm: "00" },
        ticket: null,
        billable: null
      }
    }
  },
  asyncComputed: {
    async ticketOptions() {
      if (!this.timeLog.client) return []
      const { data } = await this.$apollo.query({
        query: require("@/graphql/Client/GetClientTickets.gql"),
        variables: {
          clientId: this.timeLog.client.id
        }
      })
      //Set edit timeLog ticket if null
      if (this.editTimeLog && !this.timeLog.ticket) {
        setTimeout(() => {
          this.setEditTicket()
        }, 0)
      }
      return (
        data.clientTickets?.map(x => {
          return { value: x, text: x.description }
        }) ?? []
      )
    }
  },
  computed: {
    ...mapGetters({
      getCurrentUser: "getCurrentUser"
    }),
    billingOptions() {
      return [
        { value: 1, text: "Billable" },
        { value: 2, text: "Non-Billable" }
      ]
    },
    clientOptions() {
      return (
        this.parentClientOptions ??
        this.clients?.map(x => {
          return { value: x, text: x.name }
        })
      )
    },
    timeLogClient() {
      return this.timeLog?.client
    },
    projectOptions() {
      return (
        this.timeLogClient?.projects.map(x => {
          return {
            value: x.id,
            text: `${x.project} ${x.status != "Accepted" ? "- Closed" : ""}`
          }
        }) ?? []
      )
    }
  },
  watch: {
    timeLogClient() {
      if (!this.editTimeLog) this.timeLog.project = this.quoteId || -1
    },
    clients() {
      this.setTimeLogClient()
    },
    ticketOptions() {
      this.setTimeLogTicket()
    }
  },
  methods: {
    /**Handles submit click on timeLog modal */
    handleModal() {
      if (this.editTimeLog) this.updateTimeLog()
      else this.logTime()
    },
    /**Converts timepicker time to minutes */
    convertTime() {
      const time =
        parseFloat(this.timeLog.time.HH * 60) + parseFloat(this.timeLog.time.mm)
      if (!time) throw new Error("Please input Time.")
      return time
    },
    /**Validates form and attempts to update timeLog */
    async updateTimeLog() {
      //Validate Form
      if (this.validateTimeLogForm() !== true) return

      //Validation passes - save time log
      this._updateTimeLog()
        .then(() => {
          this.$toasted.success("Time log updated")
          this.$emit("timeLog-updated")
          this.$refs.timeLogModal.hide()
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
    },
    /**Performs the actual updateTimeLog mutation. */
    async _updateTimeLog() {
      //Convert time to minutes
      const time = this.convertTime()

      return await this.$apollo.mutate({
        mutation: gql`
          mutation updateTimeLog($timeLog: TimeLogInputType!) {
            updateTimeLog(timeLog: $timeLog)
          }
        `,
        variables: {
          timeLog: {
            id: this.editTimeLog.id,
            clientId: this.timeLog.client.id,
            userEmail: this.editTimeLog.user.email,
            description: this.timeLog.description,
            task: this.timeLog.task,
            time: time,
            date: this.timeLog.date.asGMT(),
            projectId: this.timeLog.project,
            billable: this.timeLog.billable,
            ticketId: this.timeLog.ticket?.id
          }
        }
      })
    },
    /**If the state of the modal is edit, prefills timeLog data */
    async editOrCreate() {
      if (this.editTimeLog) {
        setTimeout(async () => {
          this.timeLog.client = this.clientOptions.find(
            x => x.value.id == this.editTimeLog.client.id
          ).value
          this.timeLog.project = this.editTimeLog.project?.id ?? -1
          this.timeLog.task = this.editTimeLog.task
          this.timeLog.user = this.editTimeLog.user
          this.timeLog.description = this.editTimeLog.description
          this.timeLog.date = new Date(this.editTimeLog.date)
          this.timeLog.time = this.getTime()
          this.timeLog.billable = this.billingOptions.find(
            x => x.text == this.editTimeLog.type
          ).value
          this.setEditTicket()
        }, 0)
      }
    },
    /**
     * Sets timeLog ticket based on edittimeLog time
     */
    setEditTicket() {
      this.timeLog.ticket =
        this.ticketOptions.find(
          x =>
            x.value.ticketNumber == this.editTimeLog.ticketNumber ||
            x.value.id == this.editTimeLog.ticketNumber
        )?.value || null

      this.$forceUpdate()
    },
    /**
     * Sets timeLog time based on edittimeLog time
     */
    getTime() {
      const hours = parseInt(this.editTimeLog.time) || 0
      const minutes = (this.editTimeLog.time * 60) % 60 || 0
      const minutesString = minutes.toString()
      const hoursString = hours.toString()
      return {
        HH: `${hoursString.length == 1 ? "0" : ""}${hoursString}`,
        mm: `${minutesString.length == 1 ? "0" : ""}${minutesString}`
      }
    },
    /**
     * Shows time log modal and pre-fills based on task
     */
    show() {
      //Lazy loading clients
      this.loadClients = true
      setTimeout(() => {
        //Set task/ticket description if not null
        this.timeLog.task = this.task?.description
        //Show the modal
        this.$refs.timeLogModal.show()
      }, 500)
    },
    /**
     * Gets user time logged for today
     * @param user
     * @returns {string|number}
     */
    getLogsForToday(user) {
      const timeArray =
        user.recentTimeLogs
          ?.filter(x => this.$moment(x.date).isSame(this.$moment(), "day"))
          ?.map(x => x.time) ?? []
      if (!timeArray.length) return 0
      let time =
        timeArray.reduce(
          (accumulator, currentValue) => accumulator + currentValue
        ) ?? 0
      return time.toFixed(2)
    },
    async getTimeLogTicket(ticketNumber) {
      if (!ticketNumber) return null
      const { data } = await this.$apollo.query({
        query: require("@/graphql/TimeLog/GetTimeLogTicket.gql"),
        variables: {
          ticketNumber
        }
      })

      return data.getTimeLogTicket
    },
    /**
     * Sets the client and ticket if applicable
     */
    setTimeLogClient() {
      //If no client Id was passed through (sidebar)
      //..set client to null
      this.timeLog.client = this.clientId
        ? this.clients?.find(x => x.id === this.clientId)
        : null
    },
    setTimeLogTicket() {
      this.timeLog.ticket = this.isTicket
        ? this.ticketOptions.find(x => x.value.id == this.task.id).value
        : null
    },
    /**
     * Clears time log form and resets client
     */
    clearTimeLogForm() {
      this.timeLog = {
        client: null,
        project: -1, // Default to Support
        task: null,
        ticket: null,
        description: null,
        date: this.$moment()._d,
        time: { HH: "00", mm: "00" },
        billable: null
      }
      this.$emit("clear-edit-timeLog")
      this.setTimeLogClient()
    },
    /**
     * Validates time log form and performs save
     * @returns {Promise<ToastObject>}
     */
    // eslint-disable-next-line no-undef
    logTime: lodash.debounce(async function() {
      //Validate Form
      if (this.validateTimeLogForm() !== true) return

      //Validation passes - save time log
      this._logTime()
        .then(async () => {
          this.$toasted.success("Time logged")
          if (process.env.NODE_ENV != "development") {
            this.$refs.timeLogModal.hide()
            this.clearTimeLogForm()
          }
          await this.$apollo.queries.user.refetch()
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
    }, 2000),
    /**Validates the timeLog form. True if passed
     * @returns {Boolean} */
    validateTimeLogForm() {
      if (this.isTicket && !this.timeLog.ticket)
        return this.$toasted.error(
          "Please select the Ticket you are logging time against."
        )
      if (!this.timeLog.client)
        return this.$toasted.error("Please select a Client.")
      if (!this.timeLog.task) return this.$toasted.error("Please enter Task.")
      if (!this.timeLog.description)
        return this.$toasted.error("Please enter Description.")
      if (!this.timeLog.project)
        return this.$toasted.error("Please enter Project.")
      if (!this.timeLog.time) return this.$toasted.error("Please enter a Time.")
      if (!this.timeLog.billable)
        return this.$toasted.error("Please select Billing type.")
      if (!this.timeLog.date)
        return this.$toasted.error("Please select a Date.")

      return true
    },
    /**
     * Performs save time log mutation
     * @returns {Promise<ExecutionResult<any> & {extensions?: Record<string, any>; context?: Record<string, any>}>}
     */
    async _logTime() {
      //Convert time to minutes
      const time = this.convertTime()

      return await this.$apollo.mutate({
        mutation: gql`
          mutation logTime($timeLog: TimeLogInputType!) {
            logTime(timeLog: $timeLog)
          }
        `,
        variables: {
          timeLog: {
            clientId: this.timeLog.client.id,
            userEmail: this.getCurrentUser.email,
            description: this.timeLog.description,
            task: this.timeLog.task,
            time: time,
            date: this.timeLog.date.asGMT(),
            projectId: this.timeLog.project,
            billable: this.timeLog.billable,
            ticketId: this.timeLog.ticket?.id
          }
        }
      })
    }
  }
}
</script>

<style scoped></style>
